import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  CubeIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';

const AgentDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('/api/reports/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const agentStats = [
    {
      name: 'My Stock Value',
      value: formatCurrency(dashboardData?.stock?.totalValue),
      icon: CubeIcon,
      change: '+5%',
      changeType: 'positive',
      color: 'text-blue-600'
    },
    {
      name: 'Today\'s Sales',
      value: formatCurrency(dashboardData?.sales?.totalSales),
      icon: CurrencyDollarIcon,
      change: '+18%',
      changeType: 'positive',
      color: 'text-green-600'
    },
    {
      name: 'Outstanding Debt',
      value: formatCurrency(dashboardData?.summary?.totalDebt),
      icon: ExclamationTriangleIcon,
      change: '-12%',
      changeType: 'positive',
      color: 'text-red-600'
    },
    {
      name: 'Sales Count',
      value: `${dashboardData?.sales?.count || 0}`,
      icon: ChartBarIcon,
      change: '+8%',
      changeType: 'positive',
      color: 'text-purple-600'
    }
  ];

  const quickActions = [
    {
      name: 'Record Sale',
      href: '/agent/transactions',
      icon: CurrencyDollarIcon,
      description: 'Record a new sale',
      color: 'bg-green-500'
    },
    {
      name: 'View My Stock',
      href: '/agent/stock',
      icon: CubeIcon,
      description: 'Check current inventory',
      color: 'bg-blue-500'
    },
    {
      name: 'Manage Debts',
      href: '/agent/debts',
      icon: ExclamationTriangleIcon,
      description: 'Track shop debts',
      color: 'bg-yellow-500'
    },
    {
      name: 'View Reports',
      href: '/agent/reports',
      icon: ChartBarIcon,
      description: 'Check performance',
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <h1 className="text-2xl font-bold text-secondary-900">Agent Dashboard</h1>
        <p className="mt-1 text-sm text-secondary-600">
          Manage your stock, record sales, and track performance.
        </p>
      </div>

      {/* Agent Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {agentStats.map((stat) => (
          <div key={stat.name} className="card bg-secondary-50">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-secondary-600 truncate">
                      {stat.name}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-secondary-900">
                        {stat.value}
                      </div>
                      <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                        stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stat.changeType === 'positive' ? (
                          <ArrowUpIcon className="self-center flex-shrink-0 h-4 w-4" />
                        ) : (
                          <ArrowDownIcon className="self-center flex-shrink-0 h-4 w-4" />
                        )}
                        <span className="sr-only">
                          {stat.changeType === 'positive' ? 'Increased' : 'Decreased'} by
                        </span>
                        {stat.change}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-medium text-secondary-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-secondary-200 hover:border-secondary-300 transition-colors"
            >
              <div>
                <span className={`rounded-lg inline-flex p-3 ${action.color} text-white`}>
                  <action.icon className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium text-secondary-900">
                  {action.name}
                </h3>
                <p className="mt-2 text-sm text-secondary-500">
                  {action.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Stock Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card bg-secondary-50">
          <div className="card-header">
            <h3 className="text-lg font-medium text-secondary-900">My Current Stock</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-secondary-600">Total Units</span>
                <span className="text-sm font-medium text-secondary-900">
                  {dashboardData?.stock?.totalQuantity || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-secondary-600">Total Value</span>
                <span className="text-sm font-medium text-secondary-900">
                  {formatCurrency(dashboardData?.stock?.totalValue)}
                </span>
              </div>
              <div className="pt-2 border-t">
                <Link
                  to="/agent/stock"
                  className="text-sm text-primary-600 hover:text-primary-500 font-medium"
                >
                  View detailed stock →
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-secondary-50">
          <div className="card-header">
            <h3 className="text-lg font-medium text-secondary-900">Today's Performance</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-secondary-600">Sales Count</span>
                <span className="text-sm font-medium text-secondary-900">
                  {dashboardData?.sales?.count || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-secondary-600">Total Revenue</span>
                <span className="text-sm font-medium text-secondary-900">
                  {formatCurrency(dashboardData?.sales?.totalSales)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-secondary-600">Average Sale</span>
                <span className="text-sm font-medium text-secondary-900">
                  {formatCurrency(
                    dashboardData?.sales?.count > 0
                      ? dashboardData.sales.totalSales / dashboardData.sales.count
                      : 0,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Recent Activity</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-2 w-2 bg-green-400 rounded-full"></div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-secondary-900">Sale recorded to Shop ABC</p>
                <p className="text-xs text-secondary-500">
                  {formatCurrency(150)} - 2 hours ago
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-2 w-2 bg-blue-400 rounded-full"></div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-secondary-900">Stock updated from depot</p>
                <p className="text-xs text-secondary-500">50 units added - 4 hours ago</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-2 w-2 bg-yellow-400 rounded-full"></div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-secondary-900">Payment received from Shop XYZ</p>
                <p className="text-xs text-secondary-500">
                  {formatCurrency(200)} - 6 hours ago
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Debt Summary */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Outstanding Debts</h3>
        </div>
        <div className="card-body">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-900">Shop ABC</p>
                <p className="text-xs text-secondary-500">Due: 2 days ago</p>
              </div>
              <span className="text-sm font-medium text-red-600">{formatCurrency(300)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-900">Shop XYZ</p>
                <p className="text-xs text-secondary-500">Due: 5 days</p>
              </div>
              <span className="text-sm font-medium text-yellow-600">{formatCurrency(150)}</span>
            </div>
            <div className="pt-2 border-t">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-secondary-900">Total Outstanding</span>
                <span className="text-sm font-medium text-secondary-900">
                  {formatCurrency(dashboardData?.summary?.totalDebt)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
